// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Brandon #826 (2026-10-28, inv 2628817): "FAV 2.0 did not schedule this job or did not give the
// red border box with an explanation of why … the Estimated Received Date is 'out of range'."
//
// FileMaker's FAV script doesn't auto-schedule a job whose material is due a month or more after
// the invoice was created (John 2026-02-17, KellerInteriors DDR: `If [ $estReceiveDate <
// GetAsDate ( _creationTimestamp ) + 30 ]`) — it writes an "out of range" note and never asks
// FAV, so FAV 2.0 has no answer to draw a red box from. The Scheduler recognises the same case
// from the job itself (material date vs sold date, the day the invoice is created) and shows the
// red box + ⓘ for it. Only where FAV 2.0's screens are on; a real FAV 2.0 answer wins.
// Pure, no I/O.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const OUT_OF_RANGE_DAYS = 30;
export const OUT_OF_RANGE_REASON = "material_out_of_range";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "M/D/YYYY" (FileMaker) or "YYYY-MM-DD" → a local-midnight Date, else null. */
export function parseJobDate(s) {
  const t = String(s == null ? "" : s).trim();
  let m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return null;
}

const dayText = (d) => `${DOW[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

/**
 * The red-box skip for a job FileMaker won't auto-schedule because its material is too far out,
 * or null. `job` = the grid's job (schedule.context: companyCode, surface, statusCode, start,
 * installer, estReceivedDate, soldDate, mongoId).
 */
export function materialOutOfRangeSkip(job) {
  if (!job) return null;
  if (String(job.companyCode || "").trim().toUpperCase() !== "KI") return null;
  if (String(job.surface || "").trim().toLowerCase() !== "carpet") return null; // FileMaker's FAV call is for carpet
  const sc = parseFloat(job.statusCode);
  if (!(sc > 0) || sc >= 4) return null; // waiting for a date and a crew only
  if (job.start || String(job.installer || "").trim()) return null;
  const est = parseJobDate(job.estReceivedDate);
  const sold = parseJobDate(job.soldDate);
  if (!est || !sold) return null;
  const days = Math.round((est - sold) / 864e5);
  if (days < OUT_OF_RANGE_DAYS) return null;
  return {
    jobId: String(job.mongoId || job.id || "").toLowerCase(),
    reason: OUT_OF_RANGE_REASON,
    why:
      `FAV didn't run for this job: the material isn't due until ${dayText(est)}, ${days} days after it was sold (${sold.getMonth() + 1}/${sold.getDate()}). ` +
      `FileMaker doesn't auto-schedule a job whose material is a month or more out, so it needs scheduling by hand.`,
    outOfRange: true,
  };
}
